import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import { useParams } from 'react-router-dom';
import { formApi } from '../../services/api';
import { WorkflowPreview } from '../../types';
import { FormRuntimeContext } from '../form-renderer/FormRuntimeContext';
import { WorkflowPreviewView } from '../workflow-preview/WorkflowPreviewView';

const StyledWrapper = styled.div`
  width: 100%;
  margin: 8px 0;

  .wp-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    color: #0f766e;
    border: 1px solid #0f766e;
    border-radius: 4px;
    padding: 7px 18px;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #0f766e;
      color: #fff;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .wp-error {
    color: #d32f2f;
    font-size: 0.8rem;
    margin-top: 8px;
  }

  .wp-result {
    margin-top: 14px;
  }

  .wp-placeholder {
    border: 1px dashed #99f6e4;
    background: #f0fdfa;
    border-radius: 8px;
    padding: 14px;
    color: #115e59;
    font-size: 0.9rem;
  }
`;

/** Turns a failed request into something the visitor can read. */
const errorMessage = (error: any): string => {
  if (error.response?.status === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  const body = error.response?.data;
  return (
    (typeof body === 'string' && body) ||
    body?.error ||
    'Could not build the preview right now. Please try again.'
  );
};

interface WorkflowPreviewBlockProps {
  data: { buttonText?: string };
  /** false on the builder canvas, true in the rendered form. */
  mutable?: boolean;
}

/**
 * Shows the visitor what creating the workflow would do with the answers they
 * have entered so far. Optional: it never blocks submitting. The structure
 * comes from the workflow service; only the sample post text is AI-written, and
 * only when the visitor asks for it.
 */
const WorkflowPreviewBlock = React.forwardRef<HTMLDivElement, WorkflowPreviewBlockProps>(
  (props, ref) => {
    const { buttonText } = props.data || {};
    const { tenantId, formId } = useParams<{ tenantId?: string; formId?: string }>();
    const { getAnswers } = useContext(FormRuntimeContext);

    const [preview, setPreview] = useState<WorkflowPreview | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sample, setSample] = useState('');
    const [sampleLoading, setSampleLoading] = useState(false);
    const [sampleError, setSampleError] = useState('');

    // The builder canvas only shows where the preview will appear.
    const isLive = props.mutable !== false && Boolean(getAnswers);

    const handlePreview = async () => {
      if (!tenantId || !formId || !getAnswers || loading) return;
      setLoading(true);
      setError('');
      setSample('');
      setSampleError('');
      try {
        setPreview(await formApi.workflowPreview(tenantId, formId, getAnswers()));
      } catch (err: any) {
        setPreview(null);
        setError(errorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    const handleSample = async () => {
      if (!tenantId || !formId || !getAnswers || sampleLoading) return;
      setSampleLoading(true);
      setSampleError('');
      try {
        setSample((await formApi.workflowSample(tenantId, formId, getAnswers())).text);
      } catch (err: any) {
        setSampleError(errorMessage(err));
      } finally {
        setSampleLoading(false);
      }
    };

    return (
      <StyledWrapper ref={ref} className="workflow-preview-field">
        {!isLive ? (
          <div className="wp-placeholder">
            Workflow preview: visitors can preview what creating the workflow will do with their
            answers.
          </div>
        ) : (
          <>
            <button type="button" className="wp-button" onClick={handlePreview} disabled={loading}>
              {loading ? (
                <>
                  <CircularProgress size={14} sx={{ color: 'inherit' }} /> Building preview…
                </>
              ) : preview ? (
                'Refresh preview'
              ) : (
                buttonText || 'Preview'
              )}
            </button>
            {error && <div className="wp-error">{error}</div>}
            {preview && (
              <div className="wp-result">
                <WorkflowPreviewView
                  preview={preview}
                  sample={sample}
                  sampleLoading={sampleLoading}
                  sampleError={sampleError}
                  onGenerateSample={handleSample}
                />
              </div>
            )}
          </>
        )}
      </StyledWrapper>
    );
  },
);

WorkflowPreviewBlock.displayName = 'WorkflowPreviewBlock';
export default WorkflowPreviewBlock;
